import React, { useState, useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { Link, useParams } from "react-router-dom";
import { Alert, Table } from "reactstrap";
import Loading from "../components/Loading";
import OrgHeader from "../components/OrgHeader";
import RoleBadge from "../components/RoleBadge";
import { getOrganisation, readSelfProfile } from "../utils/apiService";
import { canManageMembers, roleFor } from "../utils/orgRoles";
import { embedUrl, hasLocation } from "../utils/maps";
import { BUSINESS_TYPES } from "../utils/orgConstants";

const OrganisationDetailsComponent = () => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadOrganisation();
  }, [id]);

  const loadOrganisation = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const [data, self] = await Promise.all([getOrganisation(id, token), readSelfProfile(token)]);

      // Normalize the response
      const normalized = {
        id: data?.id || data?.organisationId || id,
        name: data?.name || data?.orgName || data?.organisationName || 'Unknown',
        identifier: data?.identifier || data?.orgIdentifier || '',
        latitude: data?.latitude || 0,
        longitude: data?.longitude || 0,
        businessType: data?.businessType || 1,
        memberCount: data?.memberCount || data?.members?.length || 0,
        members: data?.members || [],
        userRole: roleFor(data, self?.userId),
        createdAt: data?.createdAt || null,
      };

      setOrg(normalized);
    } catch (err) {
      setError(err?.message || "Failed to load organisation");
    } finally {
      setLoading(false);
    }
  };

  const backLink = <Link to="/organisations" className="page-back">← Organisations</Link>;

  if (loading) {
    return <Loading />;
  }

  if (error && !org) {
    return (
      <div>
        {backLink}
        <Alert color="danger">{error}</Alert>
      </div>
    );
  }

  if (!org) {
    return (
      <div>
        {backLink}
        <Alert color="warning">Organisation not found</Alert>
      </div>
    );
  }

  const businessTypeLabel = BUSINESS_TYPES.find(t => t.id === org.businessType)?.label || 'Unknown';

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <OrgHeader id={id} name={org.name} role={org.userRole} identifier={org.identifier} />

      {error && <Alert color="danger" className="mb-3">{error}</Alert>}

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">Details</div>
        </div>
        <div className="panel-body">
          <dl className="detail-list">
            <div className="detail-row">
              <dt>Location</dt>
              <dd>
                {hasLocation(org.latitude, org.longitude) ? (
                  <>
                    <div className="map-frame mb-2" style={{ height: 260, maxWidth: 560 }}>
                      <iframe
                        title={`${org.name} on the map`}
                        src={embedUrl(org.latitude, org.longitude)}
                        style={{ border: 0, width: '100%', height: '100%' }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                    <small className="text-muted">{org.latitude}, {org.longitude}</small>
                  </>
                ) : (
                  <span className="text-muted">Not set</span>
                )}
              </dd>
            </div>
            <div className="detail-row">
              <dt>Business type</dt>
              <dd><span className="tag">{businessTypeLabel}</span></dd>
            </div>
            <div className="detail-row">
              <dt>Members</dt>
              <dd>{org.memberCount}</dd>
            </div>
            {org.createdAt && (
              <div className="detail-row">
                <dt>Created</dt>
                <dd>{new Date(org.createdAt).toLocaleDateString()}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {org.members && org.members.length > 0 && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">Members</div>
            {canManageMembers(org.userRole) && org.members.length > 5 && (
              <Link to={`/organisations/${id}/members`}>View all {org.members.length}</Link>
            )}
          </div>
          <Table responsive className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {org.members.slice(0, 5).map((member, idx) => (
                <tr key={idx}>
                  <td className="cell-strong">{member.firstName} {member.lastName}</td>
                  <td className="cell-muted">{member.email || member.emailAddress}</td>
                  <td><RoleBadge role={member.role} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default withAuthenticationRequired(OrganisationDetailsComponent, {
  onRedirecting: () => <Loading />,
});
